import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";
import { RecallPermissionKind, RecallPermissionStatus } from "@/modules/electron/types";
import { electronModule } from "@/modules/electron";
import { useAppStore } from "@/store/utils/hooks";
import { useSystemAudioPermissionSynchronizer } from "./system-audio-bridge.hooks";
import { SystemAudioPermissionModal } from "@/components/desktop/system-audio/permissions/system-audio-permission-modal";

export const SystemAudioBridge = observer(() => {
  const { store } = useAppStore();

  useSystemAudioPermissionSynchronizer();

  const permissionStatus = useMemo<Partial<Record<RecallPermissionKind, RecallPermissionStatus>>>(
    () => ({
      [RecallPermissionKind.ScreenCapture]: store.audioCapture.screenCapturePermissionStatus,
      [RecallPermissionKind.Microphone]: store.audioCapture.microphonePermissionStatus,
      [RecallPermissionKind.Accessibility]: store.audioCapture.accessibilityPermissionStatus,
    }),
    [
      store.audioCapture.screenCapturePermissionStatus,
      store.audioCapture.microphonePermissionStatus,
      store.audioCapture.accessibilityPermissionStatus,
    ]
  );

  const handleRequestPermission = useCallback(async (permission: RecallPermissionKind) => {
    await electronModule.recall.requestPermission({ permission });
  }, []);

  const handleOpenPermissionPane = useCallback(async (permission: RecallPermissionKind) => {
    await electronModule.recall.openPermissionPane({ permission });
  }, []);

  const handleRestart = useCallback(async () => {
    if (electronModule.restartApp) {
      await electronModule.restartApp();
      return;
    }

    await electronModule.closeWindow();
  }, []);

  return (
    <SystemAudioPermissionModal
      isOpen={store.audioCapture.showPermissionModal}
      onClose={() => store.audioCapture.setShowPermissionModal(false)}
      permissionStatus={permissionStatus}
      onRequestPermission={handleRequestPermission}
      onOpenPermissionPane={handleOpenPermissionPane}
      onRestart={handleRestart}
    />
  );
});
